import { api, newId } from '@stageplottter/core';

/**
 * Minimal structural view of a zod schema. Every response is parsed with the shared core DTO
 * schema, which turns the REST contract into a runtime check (a mismatch throws ContractError).
 */
export interface Schema<T> {
  safeParse(
    data: unknown,
  ): { success: true; data: T } | { success: false; error: { message: string } };
}

export type ErrorBody = api.ErrorResponse;

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/** The server answered, but the body does not match the contract. */
export class ContractError extends Error {
  override readonly name = 'ContractError';
  constructor(
    readonly path: string,
    readonly issues: string,
  ) {
    super(`Response of ${path} violates the API contract: ${issues}`);
  }
}

/** No response at all (offline, DNS, CORS, server down). */
export class NetworkError extends Error {
  override readonly name = 'NetworkError';
  constructor(
    readonly path: string,
    override readonly cause: unknown,
  ) {
    super(`Network error for ${path}`);
  }
}

export interface ClientOptions {
  baseUrl?: string;
  fetch?: typeof fetch;
  /** Called on 401 (default: full-page redirect to the OIDC login with returnTo). */
  onUnauthorized?: (loginUrl: string) => void;
  /** Current location for the returnTo parameter. */
  currentPath?: () => string;
  requestId?: () => string;
}

export interface RequestOptions<T> {
  body?: unknown;
  schema?: Schema<T>;
  query?: Record<string, string | number | boolean | undefined>;
  /** Default true. Set false for calls that are allowed to be anonymous (config, share). */
  redirectOn401?: boolean;
  keepalive?: boolean;
  signal?: AbortSignal;
  /** Raw body (e.g. Blob) for uploads; skips JSON encoding. */
  rawBody?: BodyInit;
  headers?: Record<string, string>;
}

export const REQUEST_ID_HEADER = 'X-Request-Id';

export function loginUrl(returnTo: string): string {
  return `/api/v1/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

function defaultPath(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname + window.location.search;
}

function defaultUnauthorized(url: string): void {
  if (typeof window !== 'undefined') window.location.assign(url);
}

export class ApiClient {
  readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly onUnauthorized: (url: string) => void;
  private readonly currentPath: () => string;
  private readonly requestId: () => string;

  constructor(opts: ClientOptions = {}) {
    this.baseUrl = opts.baseUrl ?? '/api/v1';
    this.fetchImpl = opts.fetch ?? ((...args) => globalThis.fetch(...args));
    this.onUnauthorized = opts.onUnauthorized ?? defaultUnauthorized;
    this.currentPath = opts.currentPath ?? defaultPath;
    this.requestId = opts.requestId ?? (() => newId());
  }

  url(path: string, query?: RequestOptions<unknown>['query']): string {
    const q = query
      ? Object.entries(query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    return `${this.baseUrl}${path}${q ? `?${q}` : ''}`;
  }

  async request<T = void>(method: string, path: string, opts: RequestOptions<T> = {}): Promise<T> {
    const requestId = this.requestId();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      [REQUEST_ID_HEADER]: requestId,
      ...opts.headers,
    };
    let body: BodyInit | undefined = opts.rawBody;
    if (body === undefined && opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    let res: Response;
    try {
      res = await this.fetchImpl(this.url(path, opts.query), {
        method,
        headers,
        body,
        credentials: 'include',
        // API state (e.g. a link that was revoked and extended) must never come from the HTTP
        // cache: browsers may reuse a heuristically cached 410 without asking the server.
        cache: 'no-store',
        keepalive: opts.keepalive ?? false,
        signal: opts.signal ?? null,
      });
    } catch (err) {
      throw new NetworkError(path, err);
    }

    if (res.status === 401 && opts.redirectOn401 !== false) {
      this.onUnauthorized(loginUrl(this.currentPath()));
    }
    if (!res.ok) throw await toApiError(res, requestId);
    if (res.status === 204 || opts.schema === undefined) return undefined as T;

    let json: unknown;
    try {
      json = await res.json();
    } catch {
      throw new ContractError(path, 'body is not valid JSON');
    }
    const parsed = opts.schema.safeParse(json);
    if (!parsed.success) throw new ContractError(path, parsed.error.message);
    return parsed.data;
  }

  get<T>(
    path: string,
    schema: Schema<T>,
    opts: Omit<RequestOptions<T>, 'schema'> = {},
  ): Promise<T> {
    return this.request('GET', path, { ...opts, schema });
  }
  post<T = void>(
    path: string,
    body?: unknown,
    schema?: Schema<T>,
    opts: RequestOptions<T> = {},
  ): Promise<T> {
    return this.request<T>('POST', path, { ...opts, body, ...(schema ? { schema } : {}) });
  }
  patch<T = void>(path: string, body: unknown, schema?: Schema<T>): Promise<T> {
    return this.request<T>('PATCH', path, { body, ...(schema ? { schema } : {}) });
  }
  put<T = void>(path: string, body: unknown, schema?: Schema<T>): Promise<T> {
    return this.request<T>('PUT', path, { body, ...(schema ? { schema } : {}) });
  }
  delete<T = void>(path: string, opts: RequestOptions<T> = {}): Promise<T> {
    return this.request<T>('DELETE', path, opts);
  }
}

async function toApiError(res: Response, requestId: string): Promise<ApiError> {
  const rid = res.headers.get(REQUEST_ID_HEADER) ?? requestId;
  let raw: unknown = null;
  try {
    raw = await res.json();
  } catch {
    raw = null;
  }
  const parsed = api.ErrorResponse.safeParse(raw);
  if (parsed.success) {
    return new ApiError(
      res.status,
      parsed.data.error,
      parsed.data.message,
      rid,
      parsed.data.details,
    );
  }
  return new ApiError(
    res.status,
    `http_${res.status}`,
    res.statusText || `HTTP ${res.status}`,
    rid,
  );
}

/**
 * List endpoints return a JSON array. To stay tolerant to a later paging wrapper we also accept
 * `{ items: [...] }`. Each element is still validated against the entity schema.
 */
export function listOf<T>(schema: Schema<T>): Schema<T[]> {
  return {
    safeParse(data) {
      const arr = Array.isArray(data)
        ? data
        : data !== null &&
            typeof data === 'object' &&
            Array.isArray((data as { items?: unknown }).items)
          ? (data as { items: unknown[] }).items
          : null;
      if (arr === null) return { success: false, error: { message: 'expected an array' } };
      const out: T[] = [];
      for (let i = 0; i < arr.length; i++) {
        const r = schema.safeParse(arr[i]);
        if (!r.success) return { success: false, error: { message: `[${i}] ${r.error.message}` } };
        out.push(r.data);
      }
      return { success: true, data: out };
    },
  };
}

export function isApiError(err: unknown, status?: number): err is ApiError {
  return err instanceof ApiError && (status === undefined || err.status === status);
}
